
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { CustomerDetailsSheet } from "@/components/customers/CustomerDetailsSheet";
import { CustomerSearch } from "@/components/customers/CustomerSearch";
import { CustomerActions } from "@/components/customers/CustomerActions";
import { CustomerTable } from "@/components/customers/CustomerTable";
import { useCustomersFromCoupons } from "@/hooks/customers/useCustomersFromCoupons";
import { addMessage, mockMessages } from "@/data/mockCustomerData";
import { CustomerFilterDialog } from "@/components/customers/CustomerFilterDialog";
import { useCustomerFilters } from "@/components/customers/useCustomerFilters";
import { useCustomerExport } from "@/components/customers/useCustomerExport";
import { Badge } from "@/components/ui/badge";
import { X, Loader2 } from "lucide-react";

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const { toast } = useToast();
  
  // Fetch real customers data from Supabase
  const { data: customers, isLoading: isLoadingCustomers, error } = useCustomersFromCoupons();
  
  const { 
    filters, 
    applyFilters, 
    clearFilters, 
    filteredCustomers,
    hasActiveFilters
  } = useCustomerFilters(customers || [], searchTerm);

  const { exportCustomers } = useCustomerExport();

  const handleOpenDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsDetailsOpen(true);
  };

  const handleSendWhatsAppMessage = (customerPhone, message, template = "") => {
    // Integrate with WhatsApp API
    toast({
      title: "Mensagem enviada",
      description: `Mensagem enviada para ${customerPhone}`,
    });
    
    // Add the new message to mockMessages
    const newMessage = {
      id: `m${mockMessages.length + 1}`,
      customerId: selectedCustomer.id,
      content: message,
      date: new Date().toISOString(),
      status: "Enviada",
      template: template
    };
    
    // Add the message to our mock data store
    addMessage(newMessage);
  };
  
  const handleExport = (format: 'txt' | 'csv' | 'xlsx') => {
    exportCustomers(filteredCustomers, format);
  };
  
  const handleApplyFilters = (newFilters) => {
    applyFilters(newFilters);
    setIsFilterDialogOpen(false);
    
    toast({
      title: "Filtros aplicados",
      description: "A lista de clientes foi filtrada com sucesso",
    });
  };
  
  const handleClearFilters = () => {
    clearFilters();
    setIsFilterDialogOpen(false);
    
    toast({
      title: "Filtros limpos",
      description: "Todos os filtros foram removidos",
    });
  };

  const renderActiveFilterBadges = () => {
    if (!hasActiveFilters) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {filters.nameFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Nome: {filters.nameFilter}
          </Badge>
        )}
        {filters.platformFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Plataforma: {filters.platformFilter}
          </Badge>
        )}
        {filters.statusFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Status: {filters.statusFilter}
          </Badge>
        )}
        {filters.dddFilter && (
          <Badge variant="outline" className="px-2 py-1">
            DDD: {filters.dddFilter}
          </Badge>
        )}
        {filters.minSpendFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Gasto mínimo: R${filters.minSpendFilter}
          </Badge>
        )}
        {filters.maxSpendFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Gasto máximo: R${filters.maxSpendFilter}
          </Badge>
        )}
        {filters.minPurchasesFilter && (
          <Badge variant="outline" className="px-2 py-1">
            Mínimo de compras: {filters.minPurchasesFilter}
          </Badge>
        )}
        <button 
          onClick={handleClearFilters}
          className="inline-flex items-center rounded-full bg-secondary/80 px-2 py-1 text-xs font-semibold text-secondary-foreground hover:bg-secondary/60"
        >
          Limpar filtros <X className="ml-1" size={14} />
        </button>
      </div>
    );
  };
  
  // Handle error state
  if (error) {
    return (
      <div className="space-y-8">
        <header>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-gray-500">Gerencie e analise seus clientes</p>
        </header>
        <div className="text-center py-8">
          <p className="text-red-500">Erro ao carregar clientes. Tente novamente.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold">Clientes</h1>
        <p className="text-gray-500">Gerencie e analise seus clientes da loja</p>
      </header>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="flex flex-1 gap-4 flex-col md:flex-row w-full md:w-auto">
          <CustomerSearch searchTerm={searchTerm} onChange={setSearchTerm} />
          <CustomerActions 
            onFilterClick={() => setIsFilterDialogOpen(true)} 
            onExport={handleExport} 
          />
        </div>
      </div>

      {renderActiveFilterBadges()}

      {isLoadingCustomers ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando clientes...</span>
        </div>
      ) : customers?.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Nenhum cliente encontrado na sua loja.</p>
        </div>
      ) : (
        <CustomerTable customers={filteredCustomers} onViewDetails={handleOpenDetails} />
      )}

      {selectedCustomer && (
        <CustomerDetailsSheet
          customer={selectedCustomer}
          open={isDetailsOpen}
          onOpenChange={setIsDetailsOpen}
          onSendMessage={handleSendWhatsAppMessage}
        />
      )}
      
      <CustomerFilterDialog
        open={isFilterDialogOpen}
        onOpenChange={setIsFilterDialogOpen}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialFilters={filters}
      />
    </div>
  );
};

export default Customers;
